import type { Database } from "@punchup/data/supabase.types";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { SentryFeatureTags, logException } from "@punchup/sentry";
import {
  VenuePageThemeColors,
  VenuePagesRow,
  getVenuePageTheme,
} from "../../data/venue_pages";
import {
  getTextColorForBackground,
  getTextColorForNav,
} from "@punchup/utils/colorContrast";

import { SupabaseClient } from "@supabase/supabase-js";

export interface CustomVenuePageTheme {
  accentColor: string;
  headerBackgroundColor: string;
  headerBorderColor: string;
  logoImageBackgroundColor: string;
  topSectionBackgroundColor: string;
  topSectionGradientColor: string | null;
}

interface UseVenuePageThemeValue extends CustomVenuePageTheme {
  buttonLabelColor: string;
  headerTextColor: string;
  navTextColor: string;
}

type UseVenuePageThemeParams = VenuePagesRow["slug"];

export const useVenuePageTheme = (
  venuePageSlug: UseVenuePageThemeParams
): UseVenuePageThemeValue => {
  // Fetch theme data from database
  const queryFn = async (): Promise<VenuePageThemeColors | null> => {
    const supabase = createClientComponentClient<Database>();

    try {
      const theme = await getVenuePageTheme(supabase, venuePageSlug);
      return theme;
    } catch (error) {
      logException(error as Error, {
        extra: {
          data: `error fetching theme for venue page: ${venuePageSlug}`,
        },
        tags: {
          feature: SentryFeatureTags.VenuePages,
          supabase: true,
        },
      });
      return null; // Return null instead of throwing to use fallback theme
    }
  };
  const { data, isLoading: isThemeLoading } = useQuery({
    queryKey: ["venue-page-theme", venuePageSlug],
    queryFn,
    staleTime: 60 * 1000, // 1 minute — a hydrated theme is not refetched on mount
  });

  let theme;
  if (data) {
    // Use database theme
    theme = {
      accentColor: data.accent_color,
      headerBackgroundColor: data.header_background_color,
      headerBorderColor: data.header_border_color,
      logoImageBackgroundColor: data.logo_background_color,
      topSectionBackgroundColor: data.top_section_background_color,
      topSectionGradientColor: data.top_section_gradient_color,
    };
  } else {
    // fallback to our hardcoded theme if the data isn't defined
    theme = baseTheme;
  }

  // Validate hex color format
  const isValidHex = (color: string): boolean => {
    return /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color.replace("#", ""));
  };

  // Handle loading state - return theme with sensible defaults
  // Validate colors and use fallbacks if invalid
  const headerBackgroundColor = isValidHex(theme.headerBackgroundColor)
    ? theme.headerBackgroundColor
    : baseTheme.headerBackgroundColor;

  const accentColor = isValidHex(theme.accentColor)
    ? theme.accentColor
    : baseTheme.accentColor;

  const topSectionBackgroundColor = isValidHex(theme.topSectionBackgroundColor)
    ? theme.topSectionBackgroundColor
    : baseTheme.topSectionBackgroundColor;

  const headerTextColor = getTextColorForBackground(headerBackgroundColor);
  const navTextColor = getTextColorForNav(topSectionBackgroundColor);
  const buttonLabelColor = getTextColorForBackground(accentColor);

  return {
    ...theme,
    // Override with validated colors
    headerBackgroundColor,
    accentColor,
    // append the calculated values
    headerTextColor,
    buttonLabelColor,
    navTextColor,
  };
};

// this is the "base" theme that we can fall back to
const baseTheme: CustomVenuePageTheme = {
  accentColor: "#FF5E25",
  headerBackgroundColor: "#F5F0E8",
  headerBorderColor: "#e5e5e5",
  logoImageBackgroundColor: "#000000",
  topSectionBackgroundColor: "#ffffff",
  topSectionGradientColor: null,
};

export async function prefetchVenuePageTheme(
  queryClient: QueryClient,
  supabase: SupabaseClient<Database>,
  venuePageSlug: string
) {
  await queryClient.prefetchQuery({
    queryKey: ["venue-page-theme", venuePageSlug],
    queryFn: async () => {
      try {
        return await getVenuePageTheme(supabase, venuePageSlug);
      } catch (error) {
        // Return null if no theme found instead of throwing
        logException(`Failed to retrieve theme for venue`, {
          tags: {
            feature: SentryFeatureTags.VenuePages,
            venuePageSlug,
          },
        });
        return null;
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - themes don't change often
  });
}

/**
 * Seed the useVenuePageTheme query from a theme the caller already holds
 * (e.g. the Redis-cached fallback-applied read), so the layout does not query
 * the theme row twice. The fallback colours equal baseTheme, so a venue with no
 * theme row renders the same as it would from a null query result.
 */
export function hydrateVenuePageTheme(
  queryClient: QueryClient,
  venuePageSlug: string,
  theme: VenuePageThemeColors
) {
  queryClient.setQueryData(["venue-page-theme", venuePageSlug], theme);
}
